<template>
  <!-- Skip to main content for keyboard users -->
  <a href="#main-content" class="visually-hidden skip-link">Skip to main content</a>

  <div id="main-content" class="artist-profile-edit" role="main">
    <!-- Header Section -->
    <header class="edit-header">
      <button @click="goBack" class="back-to-dashboard-btn" aria-label="Cancel and go back to dashboard">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
        Cancel
      </button>
      <h1 class="page-title">Edit Artist Profile</h1>
    </header>

    <!-- Main Content -->
    <div class="profile-content">
      
      <!-- Basic Information -->
      <section class="profile-section welcome-box" role="region" aria-labelledby="basic-info-title">
        <h2 id="basic-info-title" class="section-title">Basic Information</h2>
        
        <div class="profile-header-layout">
          <div class="profile-picture-section">
            <button class="profile-picture-container" :style="profileContainerStyle" @click="openProfileImageModal" aria-label="Click to edit profile picture">
              <div
                v-if="formData.profilePictureURL"
                :style="profileImageStyle"
                :aria-label="`Profile picture of ${displayName}`"
                role="img"
              ></div>
              <svg
                v-else
                class="default-profile-svg"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <circle cx="12" cy="7" r="5" stroke="#C1C1C1" stroke-width="2"/>
                <path d="M17 14H17.3517C18.8646 14 20.1408 15.1266 20.3285 16.6279L20.719 19.7519C20.8682 20.9456 19.9374 22 18.7344 22H5.26556C4.06257 22 3.1318 20.9456 3.28101 19.7519L3.67151 16.6279C3.85917 15.1266 5.13538 14 6.64835 14H7" stroke="#C1C1C1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <div class="profile-preview-overlay" aria-hidden="true">Profile Preview</div>
            </button>

            <button @click="openProfileImageModal" class="bubbles artist-bubbles small" aria-label="Edit profile picture">
              <span class="text">Edit Profile Image</span>
            </button>
          </div>
          
          <div class="profile-info">
            <div class="form-field">
              <label class="field-label" id="name-label">Name</label>
              <div class="name-inputs" role="group" aria-labelledby="name-label">
                <input
                  id="first-name"
                  v-model="formData.firstName"
                  class="form-input"
                  placeholder="First Name"
                  type="text"
                  required
                  aria-label="First name"
                  autocomplete="given-name"
                />
                <input
                  id="last-name"
                  v-model="formData.lastName"
                  class="form-input"
                  placeholder="Last Name"
                  type="text"
                  required
                  aria-label="Last name"
                  autocomplete="family-name"
                />
              </div>
            </div>

            <div class="organization-field">
              <label class="field-label">Organization/Studio <span class="optional-label">(optional)</span></label>
              <input
                v-model="formData.organization"
                class="input-field"
                type="text"
                placeholder="Gallery, Studio, Agency, or Organization"
              />
            </div>

            <div class="background-preview-container">
              <button class="background-preview" :style="backgroundPreviewStyle" @click="openBackgroundImageModal" aria-label="Click to edit background image">
                <div class="background-overlay" aria-hidden="true">Background Preview</div>
              </button>
            </div>

            <button @click="openBackgroundImageModal" class="bubbles artist-bubbles small" aria-label="Edit background image">
              <span class="text">Edit Background Image</span>
            </button>
          </div>
        </div>

      </section>

      <!-- Artist Bio Section -->
      <section class="profile-section welcome-box" role="region" aria-labelledby="bio-title">
        <h2 id="bio-title" class="section-title">Artist Bio</h2>
        <div class="field-group">
          <label class="field-label" for="bio-textarea">Tell us about yourself and your artistic journey</label>
          <textarea
            id="bio-textarea"
            v-model="formData.bio"
            class="bio-textarea"
            placeholder="Share your artistic background, inspirations, and what drives your creative work..."
            rows="4"
            maxlength="1000"
            aria-describedby="bio-char-count"
          ></textarea>
          <div class="char-counter" :class="{ 'near-limit': bioCharacterCount > 900 }">
            {{ bioCharacterCount }} / 1000
          </div>
        </div>
      </section>

      <!-- Areas of Expertise/Interest Section -->
      <div class="profile-section welcome-box">
        <div class="section-title-row">
          <h2 class="section-title">Areas of Expertise/Interest</h2>
          <span class="tag-counter" :class="{ 'at-max': formData.tagsSelected.length >= 10 }">{{ formData.tagsSelected.length }}/10</span>
        </div>

        <div class="tags-container">
          <div class="tags-list" v-if="formData.tagsSelected.length > 0">
            <div v-for="tag in formData.tagsSelected" :key="tag" class="tag" :data-theme="getTagTheme(tag)" @click="removeTag(tag)">
              <span class="tag-action">−</span>
              <span class="tag-text">{{ toTitleCase(tag) }}</span>
            </div>
          </div>
          
          <div class="tag-input-wrapper">
            <div class="tag-filter-icon-section">
              <button class="tag-filter-button" @click="toggleTagFilterDropdown" type="button"
                      aria-label="Filter tag options"
                      :aria-expanded="showTagFilterDropdown.toString()"
                      aria-haspopup="true">
                <svg viewBox="0 0 512 512" height="14" width="14" aria-hidden="true">
                  <path d="M0 416c0 17.7 14.3 32 32 32l54.7 0c12.3 28.3 40.5 48 73.3 48s61-19.7 73.3-48L480 448c17.7 0 32-14.3 32-32s-14.3-32-32-32l-246.7 0c-12.3-28.3-40.5-48-73.3-48s-61 19.7-73.3 48L32 384c-17.7 0-32 14.3-32 32zm128 0a32 32 0 1 1 64 0 32 32 0 1 1 -64 0zM320 256a32 32 0 1 1 64 0 32 32 0 1 1 -64 0zm32-80c-32.8 0-61 19.7-73.3 48L32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l246.7 0c12.3 28.3 40.5 48 73.3 48s61-19.7 73.3-48l54.7 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-54.7 0c-12.3-28.3-40.5-48-73.3-48zM192 128a32 32 0 1 1 0-64 32 32 0 1 1 0 64zm73.3-64C253 35.7 224.8 16 192 16s-61 19.7-73.3 48L32 64C14.3 64 0 78.3 0 96s14.3 32 32 32l86.7 0c12.3 28.3 40.5 48 73.3 48s61-19.7 73.3-48L480 128c17.7 0 32-14.3 32-32s-14.3-32-32-32L265.3 64z" fill="currentColor"/>
                </svg>
              </button>
              <div v-if="showTagFilterDropdown" class="tag-filter-dropdown">
                <div class="filter-section">
                  <div class="filter-header">Filter by Type</div>
                  <div class="filter-option" @click.stop="setTagFilter('all')" :class="{ active: tagFilter === 'all' }">
                    <span class="filter-option-text">All Tags</span>
                    <span v-if="tagFilter === 'all'" class="filter-check">✓</span>
                  </div>
                  <div class="filter-option" @click.stop="setTagFilter('art')" :class="{ active: tagFilter === 'art' }">
                    <span class="filter-option-text filter-art">Art</span>
                    <span v-if="tagFilter === 'art'" class="filter-check">✓</span>
                  </div>
                  <div class="filter-option" @click.stop="setTagFilter('science')" :class="{ active: tagFilter === 'science' }">
                    <span class="filter-option-text filter-science">Science</span>
                    <span v-if="tagFilter === 'science'" class="filter-check">✓</span>
                  </div>
                </div>
              </div>
            </div>
            <input
              type="text"
              class="tag-input"
              placeholder="Search tags..."
              v-model="tagSearchQuery"
              @keypress.enter.prevent="addTagFromInput"
            />
          </div>
          
          <div class="tag-suggestions">
            <div class="suggestions-label">{{ suggestionsLabelText }}</div>
            <div class="suggestions-pills">
              <div 
                v-for="suggestion in filteredSuggestions" 
                :key="suggestion.name" 
                class="suggestion-tag-pill" 
                :data-tag="suggestion.name" 
                :data-theme="suggestion.theme" 
                @click.stop="toggleTag(suggestion.name)"
              >
                <span class="tag-action">{{ formData.tagsSelected.includes(suggestion.name) ? '−' : '+' }}</span>
                <span class="tag-text">{{ suggestion.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Social Links Section -->
      <div class="profile-section welcome-box">
        <h2 class="section-title">Social Links</h2>
        <div class="form-grid">
          <div class="form-field">
            <label class="field-label">Portfolio Website</label>
            <input 
              v-model="formData.socialLinks.portfolio"
              class="form-input"
              placeholder="https://yourportfolio.com"
              type="url"
            />
          </div>
          <div class="form-field">
            <label class="field-label">Instagram</label>
            <input 
              v-model="formData.socialLinks.instagram"
              class="form-input"
              placeholder="@yourusername"
              type="text"
            />
          </div>
          <div class="form-field">
            <label class="field-label">Twitter/X</label>
            <input 
              v-model="formData.socialLinks.twitter"
              class="form-input"
              placeholder="@yourusername"
              type="text"
            />
          </div>
        </div>

        <!-- Other Links Section -->
        <div class="form-field">
          <label class="field-label">Other</label>
        </div>
        <div
          v-for="(link, index) in formData.socialLinks.otherLinks"
          :key="index"
          class="form-field other-link-group"
        >
          <div class="other-link-header">
            <input
              v-model="link.title"
              class="link-title-input"
              type="text"
              placeholder="Link Title"
            />
            <button
              v-if="formData.socialLinks.otherLinks.length > 1"
              @click="removeOtherLink(index)"
              class="remove-link-btn"
              type="button"
              aria-label="Remove this link"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          <input
            v-model="link.url"
            class="form-input"
            type="url"
            placeholder="https://..."
          />
        </div>

        <!-- Add More Links Button -->
        <button
          v-if="canAddMoreLinks"
          @click="addOtherLink"
          class="add-link-btn"
          type="button"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Add Link
        </button>
      </div>

      <!-- Awards & Notable Work Section (Optional) -->
      <section class="profile-section welcome-box" role="region" aria-labelledby="awards-title">
        <h2 id="awards-title" class="section-title">Awards & Notable Work <span class="optional-label">(Optional)</span></h2>
        <div class="field-group">
          <label class="field-label" for="awards-textarea">Share exhibitions, awards, publications, or notable projects</label>
          <textarea
            id="awards-textarea"
            v-model="formData.awards"
            class="bio-textarea"
            placeholder="List your exhibitions, awards, features in publications, notable collaborations, or other achievements..."
            rows="4"
            maxlength="500"
            aria-describedby="awards-char-count"
          ></textarea>
          <div class="char-counter" :class="{ 'near-limit': awardsCharacterCount > 450 }">
            {{ awardsCharacterCount }} / 500
          </div>
        </div>
      </section>

      <!-- Portfolio Section -->
      <div class="profile-section welcome-box">
        <h2 class="section-title">Portfolio</h2>
        <p class="section-helper-text">Click and drag to reorder</p>
        <div class="portfolio-management">
          <div class="portfolio-grid" v-if="formData.portfolio && formData.portfolio.length > 0">
            <div 
              v-for="(item, index) in formData.portfolio" 
              :key="item.id || index"
              class="portfolio-item"
              :draggable="true"
              @dragstart="dragStart($event, index)"
              @dragover="dragOver"
              @drop="dragDrop($event, index)"
            >
              <div class="portfolio-image" 
                   :style="getPortfolioImageStyle(item)"
                   :aria-label="item.title || 'Portfolio item'"
                   role="img">
                <div class="portfolio-overlay">
                  <button @click="editPortfolioItem(index)" class="overlay-btn edit-btn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </button>
                  <button @click="removePortfolioItem(index)" class="overlay-btn remove-btn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="3,6 5,6 21,6"/>
                      <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"/>
                    </svg>
                  </button>
                </div>
              </div>
              <div class="portfolio-info">
                <input 
                  v-model="item.title" 
                  class="portfolio-title-input" 
                  placeholder="Title"
                  @input="updatePortfolioItem"
                />
                <textarea
                  v-model="item.caption"
                  class="portfolio-caption-input"
                  placeholder="Caption (optional)"
                  rows="2"
                  maxlength="500"
                  @input="updatePortfolioItem"
                ></textarea>
                <span class="caption-char-counter" :class="{ 'near-limit': item.caption && item.caption.length > 450 }">
                  {{ item.caption ? item.caption.length : 0 }} / 500
                </span>
              </div>
            </div>
          </div>
          
          <div class="add-portfolio-item" @click="openPortfolioImageModal">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12,8 12,16"/>
              <polyline points="8,12 16,12"/>
            </svg>
            <span>Add Portfolio Item</span>
          </div>
        </div>
      </div>


      <!-- Success/Error Messages -->
      <div v-if="errorMessage" class="error-message" role="alert" aria-live="polite" aria-atomic="true">{{ errorMessage }}</div>
      <div v-if="successMessage" class="success-message" role="status" aria-live="polite" aria-atomic="true">{{ successMessage }}</div>

      <!-- Action Buttons -->
      <div class="action-section">
        <button @click="saveProfile" class="bubbles artist-bubbles save-btn" :disabled="isSaving">
          <span class="text">{{ isSaving ? 'Saving...' : 'Save Changes' }}</span>
        </button>
        <button @click="goBack" class="simple-cancel-btn">
          Cancel
        </button>
      </div>
    </div>

    <!-- Modals -->
    <ImageUploadModal
      v-if="showProfileImageModal"
      title="Upload Profile Picture"
      @close="showProfileImageModal = false"
      @upload="handleProfileImageUpload"
      :currentImage="formData.profilePictureURL"
      :savedZoom="formData.profileImageZoom || 1.0"
      :savedPositionX="formData.profileImagePositionX || 50"
      :savedPositionY="formData.profileImagePositionY || 50"
      :savedCropShape="formData.profileImageCropShape || 'circle'"
      :savedAspectRatio="formData.profileImageAspectRatio || 'free'"
      :savedCornerRadius="formData.profileImageCornerRadius || 16"
      overlayShape="circle"
      imageType="profile"
      accentColor="artist"
    />
    <ImageUploadModal
      v-if="showBackgroundImageModal"
      title="Upload Background Image"
      @close="showBackgroundImageModal = false"
      @upload="handleBackgroundImageUpload"
      :currentImage="formData.backgroundImageURL"
      :savedZoom="formData.backgroundImageZoom || 1.0"
      :savedPositionX="formData.backgroundImagePositionX || 50"
      :savedPositionY="formData.backgroundImagePositionY || 50"
      :savedCropShape="formData.backgroundImageCropShape || 'rectangle'"
      :savedAspectRatio="formData.backgroundImageAspectRatio || 'free'"
      :savedCornerRadius="formData.backgroundImageCornerRadius || 16"
      overlayShape="rectangle"
      imageType="background"
      accentColor="artist"
    />

    <ImageUploadModal
      v-if="showPortfolioImageModal"
      title="Add Image"
      @close="showPortfolioImageModal = false"
      @upload="handlePortfolioImageUpload"
      :currentImage="editingPortfolioIndex >= 0 ? formData.portfolio[editingPortfolioIndex]?.imageUrl : null"
      :savedZoom="editingPortfolioIndex >= 0 ? formData.portfolio[editingPortfolioIndex]?.zoom || 1.0 : 1.0"
      :savedPositionX="editingPortfolioIndex >= 0 ? formData.portfolio[editingPortfolioIndex]?.positionX || 50 : 50"
      :savedPositionY="editingPortfolioIndex >= 0 ? formData.portfolio[editingPortfolioIndex]?.positionY || 50 : 50"
      overlayShape="rectangle"
      imageType="portfolio"
      accentColor="artist"
      :showMetadataFields="true"
      :title-field="editingPortfolioIndex >= 0 ? formData.portfolio[editingPortfolioIndex]?.title : ''"
      :caption-field="editingPortfolioIndex >= 0 ? formData.portfolio[editingPortfolioIndex]?.caption : ''"
    />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { doc, getDoc, updateDoc } from 'firebase/firestore'
import { onAuthStateChanged } from 'firebase/auth'
import { auth, db } from '../firebase'
import ImageUploadModal from './ImageUploadModal.vue'
import { useTags } from '@/composables/useTags.js'

export default {
  name: 'ArtistProfileEdit',
  components: {
    ImageUploadModal
  },
  setup() {
    // Get dynamic tags from composable (Firestore-backed)
    const { tags: availableTags, getTagTheme } = useTags()
    const router = useRouter()
    const user = ref(null)
    const isSaving = ref(false)
    const successMessage = ref('')
    const errorMessage = ref('')
    const showProfileImageModal = ref(false)
    const showBackgroundImageModal = ref(false)
    const showPortfolioImageModal = ref(false)
    const showTagModal = ref(false)
    const editingTags = ref([])
    const tagSearchQuery = ref('')
    const tagFilter = ref('all') // 'all', 'art', 'science'
    const showTagFilterDropdown = ref(false)
    const editingBio = ref(false)
    const editingMission = ref(false)
    const bioTextarea = ref(null)
    const missionTextarea = ref(null)
    const editingPortfolioIndex = ref(-1)
    const draggedIndex = ref(-1)

    const formData = reactive({
      firstName: '',
      lastName: '',
      organization: '',
      bio: '',
      awards: '',
      missionStatement: '',
      tagsSelected: [],
      socialLinks: {
        portfolio: '',
        instagram: '',
        twitter: '',
        otherLinks: [{ title: 'Professional Link', url: '' }]
      },
      profilePictureURL: '',
      profileImageZoom: 1,
      profileImagePositionX: 50,
      profileImagePositionY: 50,
      profileImageCropShape: 'circle',
      profileImageAspectRatio: 'square',
      profileImageCornerRadius: 16,
      backgroundImageURL: '',
      backgroundImageZoom: 1,
      backgroundImagePositionX: 50,
      backgroundImagePositionY: 50,
      portfolio: []
    })

    // Default profile image
    const defaultProfile = '/src/assets/default_profile.jpg'

    // Use dynamic tags from composable - sorted alphabetically
    const predefinedSuggestions = computed(() => {
      return [...availableTags.value].sort((a, b) => a.name.localeCompare(b.name))
    })

    const displayName = computed(() => {
      const first = formData.firstName?.trim() || ''
      const last = formData.lastName?.trim() || ''
      return first && last ? `${first} ${last}` : first || last || 'Your Name'
    })

    const bioCharacterCount = computed(() => formData.bio?.length || 0)
    const awardsCharacterCount = computed(() => formData.awards?.length || 0)
    const missionCharacterCount = computed(() => formData.missionStatement?.length || 0)


    const profileImageStyle = computed(() => {
      if (!formData.profilePictureURL) {
        return {
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          backgroundColor: '#f0f0f0'
        }
      }
      
      const zoom = formData.profileImageZoom || 1
      const posX = formData.profileImagePositionX !== undefined ? formData.profileImagePositionX : 50
      const posY = formData.profileImagePositionY !== undefined ? formData.profileImagePositionY : 50
      
      const styles = {
        width: '300px',
        height: '300px',
        borderRadius: '50%',
        backgroundColor: '#ffffff', // White background for transparent PNGs
        backgroundImage: `url(${formData.profilePictureURL})`,
        backgroundSize: `${zoom * 100}%`,
        backgroundPosition: `${posX}% ${posY}%`,
        backgroundRepeat: 'no-repeat'
      }
      
      return styles
    })


    const profileContainerStyle = computed(() => {
      return {
        borderRadius: '50%'
      }
    })

    const backgroundImageStyle = computed(() => ({
      backgroundImage: formData.backgroundImageURL ? `url(${formData.backgroundImageURL})` : 'linear-gradient(135deg, var(--artColor) 0%, var(--artOutlineColor) 100%)',
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }))

    const backgroundPreviewStyle = computed(() => {
      if (!formData.backgroundImageURL) {
        return {
          backgroundImage: 'linear-gradient(135deg, var(--artColor) 0%, var(--artOutlineColor) 100%)'
        }
      }
      
      // Use the saved zoom value directly - allows zoomed out images with white space
      const zoom = formData.backgroundImageZoom || 1

      return {
        backgroundColor: '#ffffff', // White background for transparent images and zoomed-out state
        backgroundImage: `url(${formData.backgroundImageURL})`,
        backgroundSize: `${zoom * 100}%`,
        backgroundPosition: (formData.backgroundImagePositionX !== undefined || formData.backgroundImagePositionY !== undefined)
          ? `${formData.backgroundImagePositionX || 50}% ${formData.backgroundImagePositionY || 50}%`
          : 'center',
        backgroundRepeat: 'no-repeat'
      }
    })

    const filteredSuggestions = computed(() => {
      // Filter out already selected tags (case-insensitive comparison)
      const selectedLower = formData.tagsSelected.map(t => t.toLowerCase())
      let result = predefinedSuggestions.value.filter(s => !selectedLower.includes(s.name.toLowerCase()))

      // Filter by theme if not 'all'
      if (tagFilter.value !== 'all') {
        result = result.filter(s => s.theme === tagFilter.value)
      }

      // Filter by search query
      if (tagSearchQuery.value) {
        result = result.filter(s => s.name.toLowerCase().includes(tagSearchQuery.value.toLowerCase()))
      }

      return result
    })

    const suggestionsLabelText = computed(() => {
      return tagSearchQuery.value ? 'Search Results' : 'Available Tags';
    })


    const getPortfolioImageStyle = (item) => {
      if (!item.imageUrl) {
        return {
          backgroundColor: '#f0f0f0',
          backgroundImage: 'none'
        }
      }
      
      const zoom = item.zoom || 1
      const posX = item.positionX !== undefined ? item.positionX : 50
      const posY = item.positionY !== undefined ? item.positionY : 50
      
      return {
        backgroundColor: '#ffffff', // White background for transparent images
        backgroundImage: `url(${item.imageUrl})`,
        backgroundSize: `${zoom * 100}%`,
        backgroundPosition: `${posX}% ${posY}%`,
        backgroundRepeat: 'no-repeat'
      }
    }

    // Other links management (max 10 links, silently enforced)
    const canAddMoreLinks = computed(() => {
      return formData.socialLinks.otherLinks.length < 10;
    });

    const addOtherLink = () => {
      if (canAddMoreLinks.value) {
        formData.socialLinks.otherLinks.push({ title: 'Professional Link', url: '' });
      }
    };

    const removeOtherLink = (index) => {
      if (formData.socialLinks.otherLinks.length > 1) {
        formData.socialLinks.otherLinks.splice(index, 1);
      }
    };

    // Use imported getTagTheme function

    // Helper to convert text to Title Case
    const toTitleCase = (str) => {
      return str.replace(/\b\w/g, char => char.toUpperCase());
    };

    const addTag = (tagName) => {
      const trimmedTag = tagName.trim().toLowerCase();
      if (trimmedTag && !formData.tagsSelected.includes(trimmedTag) && formData.tagsSelected.length < 10) {
        formData.tagsSelected.push(trimmedTag);
      }
    }

    const addTagFromInput = () => {
      if (tagSearchQuery.value) {
        addTag(tagSearchQuery.value);
        tagSearchQuery.value = '';
      }
    }

    const toggleTag = (tagName) => {
      if (formData.tagsSelected.includes(tagName)) {
        removeTag(tagName);
      } else {
        addTag(tagName);
      }
    }

    const loadUserData = async () => {
      if (!user.value) return

      try {
        const userDoc = await getDoc(doc(db, 'users', user.value.uid))
        if (userDoc.exists()) {
          const userData = userDoc.data()
          Object.assign(formData, {
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            organization: userData.artistOrganization || '',
            bio: userData.bio || '',
            awards: userData.artistAwards || '',
            missionStatement: userData.missionStatement || '',
            tagsSelected: userData.tagsSelected || [],
            socialLinks: (() => {
              const links = userData.socialLinks || {};
              // Migrate old format (single 'other' string) to new format (otherLinks array)
              let otherLinks = links.otherLinks || [];
              if (otherLinks.length === 0) {
                // If old format exists, migrate it
                if (links.other) {
                  otherLinks = [{ title: 'Professional Link', url: links.other }];
                } else {
                  otherLinks = [{ title: 'Professional Link', url: '' }];
                }
              }
              return {
                portfolio: links.portfolio || '',
                instagram: links.instagram || '',
                twitter: links.twitter || '',
                otherLinks: otherLinks
              };
            })(),
            profilePictureURL: userData.profilePictureURL || '',
            profileImageZoom: parseFloat(userData.profileImageZoom) || 1,
            profileImagePositionX: parseFloat(userData.profileImagePositionX) || 50,
            profileImagePositionY: parseFloat(userData.profileImagePositionY) || 50,
            profileImageCropShape: userData.profileImageCropShape || 'circle',
            profileImageAspectRatio: userData.profileImageAspectRatio || 'free',
            profileImageCornerRadius: parseFloat(userData.profileImageCornerRadius) || 16,
            backgroundImageURL: userData.profileBackgroundURL || userData.backgroundImageURL || '',
            backgroundImageZoom: parseFloat(userData.backgroundImageZoom) || 1,
            backgroundImagePositionX: parseFloat(userData.backgroundImagePositionX) || 50,
            backgroundImagePositionY: parseFloat(userData.backgroundImagePositionY) || 50,
            portfolio: userData.portfolio || []
          })
        }
      } catch (error) {
        console.error('Error loading user data:', error)
      }
    }

    const saveProfile = async () => {
      if (!user.value || isSaving.value) return

      isSaving.value = true
      errorMessage.value = ''
      successMessage.value = ''
      try {
        // Create the full name from firstName and lastName
        const fullName = `${formData.firstName || ''} ${formData.lastName || ''}`.trim()
        
        // Prepare updates with explicit fields (like ScientistProfileEdit)
        // Filter out undefined values to prevent Firestore errors
        const updates = {}
        
        // Only add fields that are not undefined
        if (formData.firstName !== undefined) updates.firstName = formData.firstName
        if (formData.lastName !== undefined) updates.lastName = formData.lastName
        if (fullName) updates.name = fullName
        if (formData.organization !== undefined) updates.artistOrganization = formData.organization
        if (formData.bio !== undefined) updates.bio = formData.bio
        if (formData.awards !== undefined) updates.artistAwards = formData.awards
        if (formData.missionStatement !== undefined) updates.missionStatement = formData.missionStatement
        if (formData.tagsSelected !== undefined) updates.tagsSelected = formData.tagsSelected
        if (formData.socialLinks !== undefined) updates.socialLinks = formData.socialLinks
        updates.portfolio = formData.portfolio || []
        if (formData.profilePictureURL !== undefined) updates.profilePictureURL = formData.profilePictureURL
        if (formData.profilePictureZoom !== undefined || formData.profileImageZoom !== undefined) {
          updates.profilePictureZoom = formData.profilePictureZoom || formData.profileImageZoom || 1
        }
        if (formData.profilePicturePosition !== undefined) updates.profilePicturePosition = formData.profilePicturePosition
        if (formData.profileImageZoom !== undefined) updates.profileImageZoom = parseFloat(formData.profileImageZoom) || 1
        if (formData.profileImagePositionX !== undefined) updates.profileImagePositionX = parseFloat(formData.profileImagePositionX) ?? 50
        if (formData.profileImagePositionY !== undefined) updates.profileImagePositionY = parseFloat(formData.profileImagePositionY) ?? 50
        if (formData.profileImageCropShape !== undefined) updates.profileImageCropShape = formData.profileImageCropShape
        if (formData.profileImageAspectRatio !== undefined) updates.profileImageAspectRatio = formData.profileImageAspectRatio
        if (formData.profileImageCornerRadius !== undefined) updates.profileImageCornerRadius = formData.profileImageCornerRadius
        if (formData.backgroundImageZoom !== undefined) updates.backgroundImageZoom = parseFloat(formData.backgroundImageZoom) || 1
        if (formData.backgroundImagePositionX !== undefined) updates.backgroundImagePositionX = parseFloat(formData.backgroundImagePositionX) ?? 50
        if (formData.backgroundImagePositionY !== undefined) updates.backgroundImagePositionY = parseFloat(formData.backgroundImagePositionY) ?? 50
        if (formData.backgroundImageURL !== undefined) updates.profileBackgroundURL = formData.backgroundImageURL
        // Note: Don't overwrite role here - it should only be changed via the role slider on dashboard
        updates.verified = true // Enable gallery display for testing
        updates.updatedAt = new Date()
        
        // Also update nested profile structure for gallery compatibility
        updates.profile = {
          name: fullName,
          bio: formData.bio || '',
          profilePictureURL: formData.profilePictureURL || '',
          profileBackgroundURL: formData.backgroundImageURL || ''
        }
        
        // Add timestamp for tracking updates
        updates.updatedAt = new Date()

        await updateDoc(doc(db, 'users', user.value.uid), updates)

        // Navigate to welcome/dashboard page
        router.push('/profile-dashboard')
      } catch (error) {
        console.error('Error saving profile:', error)
        errorMessage.value = 'Failed to save profile. Please try again.'
      } finally {
        isSaving.value = false
      }
    }

    const goBack = () => {
      router.push('/profile-dashboard')
    }

    // Authentication and lifecycle logic
    onAuthStateChanged(auth, (currentUser) => {
      user.value = currentUser
      if (currentUser) {
        loadUserData()
      }
    })

    const openProfileImageModal = () => {
      showProfileImageModal.value = true
    }

    const openBackgroundImageModal = () => {
      showBackgroundImageModal.value = true
    }

    const startEditingBio = () => {
      editingBio.value = true
      nextTick(() => {
        if (bioTextarea.value) {
          bioTextarea.value.focus()
        }
      })
    }

    const stopEditingBio = () => {
      editingBio.value = false
    }

    const startEditingMission = () => {
      editingMission.value = true
      nextTick(() => {
        if (missionTextarea.value) {
          missionTextarea.value.focus()
        }
      })
    }

    const stopEditingMission = () => {
      editingMission.value = false
    }

    const handleProfileImageUpload = (imageData) => {
      // Handle both old format (string) and new format (object)
      if (typeof imageData === 'string') {
        formData.profilePictureURL = imageData
      } else {
        formData.profilePictureURL = imageData.imageUrl
        // Store zoom/position data for future edits
        formData.profileImageZoom = imageData.zoom
        formData.profileImagePositionX = imageData.positionX
        formData.profileImagePositionY = imageData.positionY
        // Store crop shape data for future edits
        formData.profileImageCropShape = imageData.cropShape
        formData.profileImageAspectRatio = imageData.aspectRatio
        formData.profileImageCornerRadius = imageData.cornerRadius
      }
      showProfileImageModal.value = false
    }

    const handleBackgroundImageUpload = (imageData) => {
      // Handle both old format (string) and new format (object)
      if (typeof imageData === 'string') {
        formData.backgroundImageURL = imageData
      } else {
        formData.backgroundImageURL = imageData.imageUrl
        // Store zoom/position data for future edits
        formData.backgroundImageZoom = imageData.zoom
        formData.backgroundImagePositionX = imageData.positionX
        formData.backgroundImagePositionY = imageData.positionY
        // Store crop shape data for future edits
        formData.backgroundImageCropShape = imageData.cropShape
        formData.backgroundImageAspectRatio = imageData.aspectRatio
        formData.backgroundImageCornerRadius = imageData.cornerRadius
      }
      showBackgroundImageModal.value = false
    }

    const editTags = () => {
      editingTags.value = [...formData.tagsSelected]
      showTagModal.value = true
    }

    const handleTagSelection = (selectedTags) => {
      formData.tagsSelected = [...selectedTags]
      showTagModal.value = false
    }

    const removeTag = (tagToRemove) => {
      formData.tagsSelected = formData.tagsSelected.filter(tag => tag !== tagToRemove)
    }

    const toggleTagFilterDropdown = () => {
      showTagFilterDropdown.value = !showTagFilterDropdown.value
    }

    const setTagFilter = (filter) => {
      tagFilter.value = filter
      showTagFilterDropdown.value = false
    }

    // Portfolio Management Functions
    const openPortfolioImageModal = () => {
      editingPortfolioIndex.value = -1
      showPortfolioImageModal.value = true
    }

    const editPortfolioItem = (index) => {
      editingPortfolioIndex.value = index
      showPortfolioImageModal.value = true
    }

    const handlePortfolioImageUpload = (imageData) => {
      const portfolioItem = {
        id: Date.now() + Math.random(),
        imageUrl: typeof imageData === 'string' ? imageData : imageData.imageUrl,
        zoom: typeof imageData === 'object' ? imageData.zoom : 1.0,
        positionX: typeof imageData === 'object' ? imageData.positionX : 50,
        positionY: typeof imageData === 'object' ? imageData.positionY : 50,
        title: imageData.title || '',
        caption: imageData.caption || ''
      }

      if (editingPortfolioIndex.value >= 0) {
        // Editing existing item
        formData.portfolio[editingPortfolioIndex.value] = {
          ...formData.portfolio[editingPortfolioIndex.value],
          ...portfolioItem
        }
      } else {
        // Adding new item
        if (!formData.portfolio) {
          formData.portfolio = []
        }
        formData.portfolio.push(portfolioItem)
      }

      showPortfolioImageModal.value = false
      editingPortfolioIndex.value = -1
    }

    const removePortfolioItem = (index) => {
      if (confirm('Are you sure you want to remove this portfolio item?')) {
        formData.portfolio.splice(index, 1)
      }
    }

    const updatePortfolioItem = () => {
      // This function is called when title/caption inputs change
      // The reactive binding already updates the data, so we don't need to do anything here
      // But we could add validation or other logic if needed
    }

    // Drag and Drop Functions
    const dragStart = (event, index) => {
      draggedIndex.value = index
      event.dataTransfer.effectAllowed = 'move'
    }

    const dragOver = (event) => {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
    }

    const dragDrop = (event, targetIndex) => {
      event.preventDefault()
      if (draggedIndex.value !== targetIndex) {
        const draggedItem = formData.portfolio[draggedIndex.value]
        formData.portfolio.splice(draggedIndex.value, 1)
        formData.portfolio.splice(targetIndex, 0, draggedItem)
      }
      draggedIndex.value = -1
    }

    onMounted(() => {
      onAuthStateChanged(auth, (currentUser) => {
        user.value = currentUser
        if (currentUser) {
          loadUserData()
        } else {
          router.push('/login')
        }
      })
    })

    return {
      formData,
      user,
      isSaving,
      successMessage,
      errorMessage,
      showProfileImageModal,
      showBackgroundImageModal,
      showPortfolioImageModal,
      showTagModal,
      editingTags,
      tagSearchQuery,
      tagFilter,
      showTagFilterDropdown,
      toggleTagFilterDropdown,
      setTagFilter,
      editingBio,
      editingMission,
      bioTextarea,
      missionTextarea,
      editingPortfolioIndex,
      draggedIndex,
      predefinedSuggestions,
      filteredSuggestions,
      suggestionsLabelText,
      displayName,
      bioCharacterCount,
      awardsCharacterCount,
      missionCharacterCount,
      profileImageStyle,
      profileContainerStyle,
      backgroundImageStyle,
      backgroundPreviewStyle,
      getPortfolioImageStyle,
      canAddMoreLinks,
      addOtherLink,
      removeOtherLink,
      defaultProfile,
      saveProfile,
      goBack,
      openProfileImageModal,
      openBackgroundImageModal,
      startEditingBio,
      stopEditingBio,
      startEditingMission,
      stopEditingMission,
      openPortfolioImageModal,
      editPortfolioItem,
      handleProfileImageUpload,
      handleBackgroundImageUpload,
      handlePortfolioImageUpload,
      removePortfolioItem,
      updatePortfolioItem,
      dragStart,
      dragOver,
      dragDrop,
      editTags,
      handleTagSelection,
      removeTag,
      getTagTheme,
      toTitleCase,
      addTag,
      addTagFromInput,
      toggleTag
    }
  }
}
</script>

<style scoped>
/* ===== ACCESSIBILITY STYLES ===== */
.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

.skip-link {
  position: absolute;
  top: -40px;
  left: 6px;
  background: var(--artColor);
  color: white;
  padding: 8px;
  text-decoration: none;
  border-radius: 4px;
  z-index: 10000;
  font-weight: 600;
  transition: top 0.2s ease;
}

.skip-link:focus {
  top: 6px;
}

.required {
  color: #dc3545;
  font-weight: bold;
  margin-left: 2px;
}

/* Enhanced focus indicators for keyboard users */
.back-to-dashboard-btn:focus:not(:focus-visible),
.bubbles:focus:not(:focus-visible),
.profile-picture-container:focus:not(:focus-visible),
.background-preview:focus:not(:focus-visible),
.editable-display:focus:not(:focus-visible),
.overlay-btn:focus:not(:focus-visible),
.add-portfolio-item:focus:not(:focus-visible),
.suggestion-tag-pill:focus:not(:focus-visible),
.tag:focus:not(:focus-visible),
.checkbox-label:focus:not(:focus-visible) {
  outline: none;
}

.back-to-dashboard-btn:focus-visible,
.bubbles:focus-visible,
.profile-picture-container:focus-visible,
.background-preview:focus-visible,
.editable-display:focus-visible,
.form-input:focus-visible,
.textarea-field:focus-visible,
.overlay-btn:focus-visible,
.add-portfolio-item:focus-visible,
.portfolio-title-input:focus-visible,
.portfolio-caption-input:focus-visible,
.suggestion-tag-pill:focus-visible,
.tag:focus-visible,
.tag-input:focus-visible,
.checkbox-input:focus-visible + .checkbox-custom {
  outline: 2px solid var(--artColor);
  outline-offset: 2px;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .profile-section {
    border: 2px solid #000;
  }

  .form-input,
  .textarea-field {
    border-width: 3px;
  }

  .tag,
  .suggestion-tag-pill {
    border-width: 2px;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .bubbles,
  .bubbles::before,
  .bubbles::after,
  .profile-picture-container,
  .background-preview,
  .portfolio-item,
  .overlay-btn,
  .tag,
  .suggestion-tag-pill {
    animation: none !important;
    transition: none !important;
  }
}

/* Touch target size for mobile */
@media (max-width: 768px) {
  .bubbles,
  .back-to-dashboard-btn,
  .overlay-btn,
  .tag,
  .suggestion-tag-pill,
  .checkbox-label {
    min-height: 44px;
    min-width: 44px;
  }

  .bubbles {
    padding: 12px 24px;
  }

  .profile-picture-container,
  .background-preview {
    min-height: 44px;
    min-width: 44px;
  }
}

/* Style editable display as button */
.editable-display {
  background: none;
  border: none;
  text-align: left;
  width: 100%;
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;
  color: inherit;
  padding: 0.75rem;
  border: 2px solid var(--borderColor);
  border-radius: calc(var(--stdRadius) / 2);
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  transition: border-color 0.2s ease;
}

.editable-display:hover {
  border-color: var(--artColor);
}

.edit-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

/* Style profile picture and background preview as buttons */
.profile-picture-container,
.background-preview {
  background: none;
  border: 2px solid #E0E0E0;
  cursor: pointer;
  transition: all 0.2s ease;
}

.profile-picture-container:hover,
.background-preview:hover {
  border-color: var(--artColor);
}

/* Use PALLAS design system CSS variables */
.artist-profile-edit {
  min-height: 100vh;
  background: transparent;
  padding: 7rem 2rem 80px 2rem;
  max-width: 1000px;
  margin: 0 auto;
}

/* Header Styles - Cancel button left, title right */
.edit-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: var(--paddingStandard, 1rem);
}

.page-title {
  font-size: clamp(1.25rem, 3vw, 1.5rem);
  font-weight: 600;
  color: var(--headersColor, #333);
  margin: 0;
}

.profile-content {
  display: flex;
  flex-direction: column;
  gap: var(--paddingStandard, 1rem);
}

/* Profile sections - Compact dashboard style */
.profile-section {
  padding: var(--paddingStandard, 1rem);
  border-radius: var(--stdRadius, 12px);
  position: relative;
  background: rgba(255, 255, 255, 0.95);
  overflow: visible;
}

.profile-section::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(90deg, #FEF9F8 0%, #FEF9F8 100%);
  border-radius: var(--stdRadius, 12px);
  z-index: -1;
  opacity: 1;
}

.profile-section::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  border-radius: var(--stdRadius, 12px);
  z-index: -1;
}

.profile-section > * {
  position: relative;
  z-index: 1;
}

.section-title {
  font-size: clamp(1rem, 2.5vw, 1.15rem);
  font-weight: 600;
  color: var(--headersColor, #333);
  margin: 1.5rem 0 0.5rem 0;
}

.optional-label {
  font-size: 0.85em;
  font-weight: 400;
  color: #888;
}

.section-title:first-child,
.profile-section:first-of-type .section-title {
  margin-top: 0;
}

.section-helper-text {
  color: #888;
  font-size: 0.8rem;
  font-style: italic;
  margin: 0 0 0.75rem 0;
}

.section-divider {
  height: 1px;
  background: linear-gradient(90deg, var(--artColor) 0%, var(--sciColor) 100%);
  margin: 1rem 0;
  opacity: 0.3;
}

.section-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--paddingStandard, 1rem);
}

.section-title-row .section-title {
  margin: 0;
}

.tag-counter {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--textColor);
  opacity: 0.7;
}

.tag-counter.at-max {
  color: var(--artColor);
  opacity: 1;
  font-weight: 600;
}

/* Profile Header Layout */
.profile-header-layout {
  display: flex;
  align-items: flex-start;
  gap: var(--paddingStandard, 1rem);
  margin-bottom: var(--paddingStandard, 1rem);
  overflow: visible;
}

.profile-picture-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
}

.profile-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-right: 20px;
}

.profile-picture-container {
  position: relative;
  cursor: pointer;
  /* border-radius: 50%; REMOVED - now set dynamically via :style */
  overflow: hidden;
  border: 2px solid #E0E0E0;
  transition: all 0.2s ease;
  width: 300px;
  height: 300px;
  min-width: 300px;
  min-height: 300px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.profile-picture-container:hover {
  border-color: var(--artColor);
}

.profile-picture-container img {
  display: block;
  width: 300px;
  height: 300px;
  object-fit: cover;
}

.default-profile-svg {
  width: 100%;
  height: 100%;
  padding: 20px;
  box-sizing: border-box;
}

.profile-preview-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 0.5rem;
  text-align: center;
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 0 0 50% 50%;
}

.background-preview-container {
  margin-bottom: 1rem;
  display: flex;
  justify-content: center;
}

.background-preview {
  width: 350px;
  height: 200px;
  border-radius: 8px;
  border: 2px solid #E0E0E0;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
}

.background-preview:hover {
  border-color: var(--artColor);
}

.background-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 0.5rem;
  text-align: center;
  font-size: 0.9rem;
  font-weight: 500;
}

/* Form Styling */
.form-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* Other Links Styling */
.other-link-group {
  margin-top: 0.75rem;
}

.other-link-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.link-title-input {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--borderColor, #ddd);
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--headersColor, #333);
  background: var(--bckColor, #f9f9f9);
  transition: border-color 0.2s ease;
}

.link-title-input:focus {
  outline: none;
  border-color: var(--artColor);
}

.link-title-input::placeholder {
  color: #999;
  font-weight: 400;
}

.remove-link-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.05);
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;
}

.remove-link-btn:hover {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
}

.add-link-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px dashed var(--borderColor, #ddd);
  border-radius: 8px;
  background: transparent;
  color: var(--artColor);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 0.75rem;
}

.add-link-btn:hover {
  border-color: var(--artColor);
  background: rgba(248, 177, 155, 0.05);
}

.field-label {
  display: block;
  color: var(--artColor);
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 0.25rem;
}

.char-count {
  font-size: 0.8rem;
  color: var(--textColor);
  font-weight: normal;
}

.char-counter {
  text-align: right;
  font-size: 0.75rem;
  color: var(--artColor);
  opacity: 0.6;
  margin-top: 4px;
}

.char-counter.near-limit {
  color: var(--artColor);
  opacity: 1;
  font-weight: 600;
}

.bio-textarea {
  width: 100%;
  padding: var(--paddingStandard);
  border: 1px solid var(--borderColor);
  border-radius: var(--stdRadius);
  font-size: var(--labelText);
  font-family: var(--font-primary);
  color: var(--textColor);
  background: white;
  resize: vertical;
  min-height: 100px;
  transition: border-color 0.2s ease;
}

.bio-textarea:focus {
  outline: none;
  border-color: var(--artColor);
}

.bio-textarea::placeholder {
  color: var(--textColor);
  opacity: 0.5;
}

.form-input, .form-textarea {
  padding: 0.75rem;
  border: 2px solid var(--borderColor);
  border-radius: calc(var(--stdRadius) / 2);
  font-family: inherit;
  font-size: 1rem;
  color: var(--textColor);
  background: var(--bckColor);
  transition: border-color 0.2s ease;
}

.form-input:focus, .form-textarea:focus {
  outline: none;
  border-color: var(--artColor);
}

.name-inputs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.organization-field {
  margin-top: 0.5rem;
}

.organization-field .input-field {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid var(--borderColor);
  border-radius: calc(var(--stdRadius) / 2);
  font-family: inherit;
  font-size: 1rem;
  transition: border-color 0.2s ease;
}

.organization-field .input-field:focus {
  outline: none;
  border-color: var(--artColor);
}

.optional-label {
  font-weight: 400;
  color: #888;
  font-size: 0.85rem;
}

/* FilterDropdown-style Tags Section */
.tags-container {
  padding: 0;
}

.tag-input-wrapper {
  display: flex;
  align-items: center;
  margin-bottom: 0px;
  background-color: var(--bckColor);
  border: none;
  border-radius: var(--stdRadius) var(--stdRadius) 0 0;
  padding: 0.5rem 0;
}

/* Filter icon section */
.tag-filter-icon-section {
  position: relative;
  display: flex;
  align-items: center;
  padding-left: 0.5rem;
}

.tag-filter-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1px solid var(--borderColor);
  border-radius: 6px;
  cursor: pointer;
  color: var(--textColor);
  transition: all 0.2s ease;
}

.tag-filter-button:hover {
  background: var(--artOutlineColor);
  border-color: var(--artColor);
  color: var(--artColor);
}

.tag-filter-button:focus {
  outline: none;
  border-color: #999;
}

.tag-filter-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid var(--borderColor);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 140px;
  overflow: hidden;
}

.filter-section {
  padding: 0.5rem 0;
}

.filter-header {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--textColor);
  opacity: 0.6;
  padding: 0.25rem 0.75rem 0.5rem;
}

.filter-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  transition: background 0.15s ease;
}

.filter-option:hover {
  background: var(--bckColor);
}

.filter-option.active {
  background: var(--bckColor);
}

.filter-option-text {
  font-size: 13px;
  color: var(--textColor);
}

.filter-option-text.filter-art {
  color: var(--artColor);
}

.filter-option-text.filter-science {
  color: var(--sciColor);
}

.filter-check {
  color: var(--artColor);
  font-weight: 600;
}

.tag-input {
  flex: 1;
  background-color: transparent;
  color: var(--textColor);
  padding: .15rem .5rem;
  min-height: 30px;
  border-radius: 0;
  outline: none;
  border: none;
  line-height: 1.15;
  font-size: 14px;
  font-family: var(--font-primary);
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.tag-input:focus {
  outline: none;
  border: none;
}

.tag-input::placeholder {
  color: var(--textColor);
  font-size: 14px;
}

/* Tag suggestions as container */
.tag-suggestions {
  background: var(--bckColor);
  border: none;
  border-radius: 0 0 var(--stdRadius) var(--stdRadius);
  padding: 8px 12px;
  margin-top: -1px;
  max-width: 100%;
  max-height: 160px;
  overflow-y: auto;
  position: relative;
}

.tag-suggestions::-webkit-scrollbar {
  width: 6px;
}

.tag-suggestions::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.tag-suggestions::-webkit-scrollbar-thumb {
  background: var(--textColor);
  border-radius: 3px;
}

.tag-suggestions::-webkit-scrollbar-thumb:hover {
  background: var(--artColor);
}

.suggestions-label {
  font-size: 11px;
  color: var(--textColor);
  margin-bottom: 6px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.8;
}

.suggestions-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.suggestion-tag-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: var(--neutralColor);
  border: var(--stdOutline) solid var(--borderColor);
  border-radius: 4px;
  font-size: 10px;
  font-family: var(--font-primary);
  color: var(--textColor);
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.suggestion-tag-pill[data-theme="art"] {
  background: var(--bckColor);
  border: var(--stdOutline) solid var(--artOutlineColor);
  color: var(--textColor);
}

.suggestion-tag-pill[data-theme="art"] .tag-action {
  color: var(--artColor);
}

.suggestion-tag-pill[data-theme="art"] .tag-text {
  color: #555;
}

.suggestion-tag-pill[data-theme="science"] {
  background: var(--bckColor);
  border: var(--stdOutline) solid var(--sciOutlineColor);
  color: var(--textColor);
}

.suggestion-tag-pill[data-theme="science"] .tag-action {
  color: var(--sciColor);
}

.suggestion-tag-pill[data-theme="science"] .tag-text {
  color: #555;
}


.suggestion-tag-pill[data-theme="art"]:hover {
  background: var(--artOutlineColor);
  border-color: var(--artColor);
}

.suggestion-tag-pill[data-theme="art"]:hover .tag-action {
  color: var(--artColor);
}

.suggestion-tag-pill[data-theme="science"]:hover {
  background: var(--sciOutlineColor);
  border-color: var(--sciColor);
}

.suggestion-tag-pill[data-theme="science"]:hover .tag-action {
  color: var(--sciColor);
}


.suggestion-tag-pill:active {
  background: #d1d5db;
}

.tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 0 var(--paddingStandard) 8px var(--paddingStandard);
  min-height: 0px;
}

.tags-list:empty {
  display: none;
}

.tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 10px;
  color: var(--textColor);
  cursor: pointer;
}

.tag[data-theme="art"] {
  background: var(--artOutlineColor);
  border: var(--stdOutline) solid var(--artColor);
  color: var(--artColor);
}

.tag[data-theme="art"] .tag-action {
  color: var(--artColor);
}

.tag[data-theme="art"] .tag-text {
  color: #555;
}

.tag[data-theme="science"] {
  background: var(--sciOutlineColor);
  border: var(--stdOutline) solid var(--sciColor);
  color: var(--sciColor);
}

.tag[data-theme="science"] .tag-action {
  color: var(--sciColor);
}

.tag[data-theme="science"] .tag-text {
  color: #555;
}


.tag[data-theme="art"]:hover {
  background: var(--bckColor);
  border-color: var(--artOutlineColor);
}

.tag[data-theme="art"]:hover .tag-action {
  color: var(--artColor);
}

.tag[data-theme="science"]:hover {
  background: var(--bckColor);
  border-color: var(--sciOutlineColor);
}

.tag[data-theme="science"]:hover .tag-action {
  color: var(--sciColor);
}


.tag .tag-action {
  cursor: pointer;
  font-weight: bold;
  font-size: 12px;
  color: var(--textColor);
  line-height: 1;
  min-width: 12px;
  text-align: center;
  transition: transform 0.2s ease;
}

.tag-action {
  font-weight: bold;
  font-size: 12px;
  color: var(--textColor);
  line-height: 1;
  min-width: 12px;
  text-align: center;
}

.tag-text {
  font-size: 10px;
  color: #555;
}

/* Portfolio Management Section */
.portfolio-management {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.portfolio-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1rem;
}

.portfolio-item {
  background: white;
  border-radius: calc(var(--stdRadius) / 2);
  border: 2px solid var(--borderColor);
  overflow: hidden;
  cursor: move;
  transition: all 0.2s ease;
  position: relative;
}

.portfolio-item:hover {
  border-color: var(--artColor);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.portfolio-item:active {
  cursor: grabbing;
}

.portfolio-image {
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
}

.portfolio-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.2s ease;
}

.portfolio-item:hover .portfolio-image img {
  transform: scale(1.05);
}

.portfolio-overlay {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.portfolio-item:hover .portfolio-overlay {
  opacity: 1;
}

.overlay-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: white;
}

.edit-btn {
  background: var(--artColor);
}

.edit-btn:hover {
  background: var(--artOutlineColor);
  transform: scale(1.1);
}

.remove-btn {
  background: #dc3545;
}

.remove-btn:hover {
  background: #c82333;
  transform: scale(1.1);
}

.portfolio-info {
  padding: 0.5rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.portfolio-title-input {
  font-size: 1rem;
  font-weight: 600;
  border: none;
  background: transparent;
  color: var(--headersColor);
  padding: 0.25rem 0;
  border-bottom: 1px solid transparent;
  transition: border-color 0.2s ease;
}

.portfolio-title-input:focus {
  outline: none;
  border-bottom-color: var(--artColor);
}

.portfolio-title-input::placeholder {
  color: #999;
  font-weight: normal;
}

.portfolio-caption-input {
  font-size: 0.9rem;
  color: var(--textColor);
  border: none;
  background: transparent;
  padding: 0.25rem 0;
  resize: vertical;
  min-height: 40px;
  border-bottom: 1px solid transparent;
  transition: border-color 0.2s ease;
}

.portfolio-caption-input:focus {
  outline: none;
  border-bottom-color: var(--artColor);
}

.portfolio-caption-input::placeholder {
  color: #999;
}

.caption-char-counter {
  font-size: 0.75rem;
  color: #999;
  text-align: right;
  margin-top: 2px;
}

.caption-char-counter.near-limit {
  color: var(--artColor);
}

.add-portfolio-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 3rem 2rem;
  border: 2px dashed var(--borderColor);
  border-radius: calc(var(--stdRadius) / 2);
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--textColor);
  background: transparent;
}

.add-portfolio-item:hover {
  border-color: var(--artColor);
  color: var(--artColor);
  background: rgba(248, 177, 155, 0.05);
}

.add-portfolio-item svg {
  transition: transform 0.2s ease;
}

.add-portfolio-item:hover svg {
  transform: scale(1.1);
}

.add-portfolio-item span {
  font-size: 1rem;
  font-weight: 500;
}


/* Action Buttons */
.action-section {
  display: flex;
  gap: var(--paddingStandard, 1rem);
  justify-content: flex-start;
  flex-wrap: wrap;
  margin-top: var(--paddingStandard, 1rem);
  padding-top: var(--paddingStandard, 1rem);
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.save-btn {
  min-width: 150px;
  background: #C1C1C1 !important;
  border-color: #C1C1C1 !important;
  transition: all 0.2s ease !important;
}

.save-btn > .text {
  color: white !important;
}

.save-btn::before,
.save-btn::after {
  display: none !important;
}

.save-btn:hover {
  background: var(--artColor) !important;
  border-color: var(--artColor) !important;
  transform: none !important;
  scale: none !important;
}

.save-btn:hover > .text {
  color: white !important;
}

/* Bubble button styling matching homepage */
.bubbles {
  --size-letter: clamp(1rem, 2.5vw, 1.1rem);
  padding: 0.75em 1.5em;
  font-size: var(--size-letter);
  font-family: var(--font-primary);
  background-color: transparent;
  border-radius: var(--stdRadius);
  cursor: pointer;
  overflow: hidden;
  position: relative;
  display: inline-block;
  transition: 150ms cubic-bezier(0.83, 0, 0.17, 1);
  border: none;
}

.bubbles > .text {
  font-weight: 600;
  position: relative;
  z-index: 1;
  transition: color 350ms cubic-bezier(0.83, 0, 0.17, 1);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.bubbles::before {
  top: 0;
  left: 0;
}

.bubbles::after {
  top: 100%;
  left: 100%;
}

.bubbles::before,
.bubbles::after {
  content: "";
  width: 150%;
  aspect-ratio: 1/1;
  scale: 0;
  transition: 500ms cubic-bezier(0.76, 0, 0.24, 1);
  border-radius: 50%;
  position: absolute;
  translate: -50% -50%;
}

.bubbles:hover {
  & > .text {
    color: var(--bckColor);
  }
  &::before,
  &::after {
    scale: 1;
  }
}

.bubbles:active {
  scale: 0.98;
  filter: brightness(0.9);
}

.bubbles:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Artist-themed bubble styling */
.artist-bubbles {
  border: calc(var(--size-letter) / 6) solid #C1C1C1;
}

.artist-bubbles > .text {
  color: var(--textColor);
}

.artist-bubbles::before,
.artist-bubbles::after {
  background: var(--artColor);
}

.artist-bubbles:hover {
  border-color: var(--artColor);
}

.artist-bubbles:hover > .text {
  color: white;
}

/* Back to dashboard button styling - matches gallery "Back to Gallery" style */
.back-to-dashboard-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: #ffffff;
  border: 1.5px solid #aaa;
  border-radius: 8px;
  color: #333;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.back-to-dashboard-btn:hover {
  background: #fafafa;
  border-color: #888;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.back-to-dashboard-btn:focus-visible {
  outline: 2px solid var(--artColor);
  outline-offset: 2px;
}

/* Simple Cancel button styling - no animation */
.simple-cancel-btn {
  padding: 0.75em 2em;
  font-size: 1rem;
  font-weight: 500;
  color: #555;
  background: #f5f5f5;
  border: 1.5px solid #ccc;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.simple-cancel-btn:hover {
  background: #555;
  color: #fff;
  border-color: #555;
}

/* Small button styling */
.bubbles.small {
  padding: 0.5em 1em;
  font-size: 0.9rem;
}

.bubbles.small .text {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Responsive Design */
@media (max-width: 768px) {
  .artist-profile-edit {
    padding: 1rem 0.5rem;
  }
  
  .profile-header {
    flex-direction: column;
    align-items: stretch;
  }
  
  .name-inputs {
    grid-template-columns: 1fr;
  }
  
  .profile-header-layout {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 1rem;
  }
  
  .action-section {
    flex-direction: column;
  }
}

/* Success/Error Messages */
.success-message {
  color: var(--successColor);
  font-size: var(--labelText);
  text-align: center;
  margin: 1rem 0;
  padding: 0.75rem;
  background: var(--successOutlineColor);
  border: 2px solid var(--successColor);
  border-radius: 8px;
  font-weight: 600;
}

.error-message {
  color: #d32f2f;
  font-size: var(--labelText);
  text-align: center;
  margin: 1rem 0;
  padding: 0.75rem;
  background: rgba(211, 47, 47, 0.1);
  border: 2px solid #d32f2f;
  border-radius: 8px;
  font-weight: 600;
}
</style>